<template>
  <!-- The secondary class "student-selection-listing-from-group-content-subpage" is being used
     - as a selector for the boundary for the filter pop-up.
     - Do not remove it unless necessary for any revamp/refactor in the future.
  -->
  <content-subpage
    class="add-recipients-content-subpage student-selection-listing-from-group-content-subpage"
    :visible="visible"
    :titles="titles"
    :has-parent-subpage="hasParentSubpage"
    :back-label="backLabel"
    @content-subpage-hidden="actionHidden(params.actionHidden)"
    @content-subpage-back="actionBack()"
  >
    <template #main-content>
      <dropdown-component
        class="class-group-dropdown"
        kind="white"
        :value="selectedClassGroup"
        @change="handleChangeClassGroupSelected"
      >
        <cv-dropdown-item
          v-for="(classGroup, index) in availableClassGroups"
          :key="index"
          :value="classGroup.groupName"
        >
          {{ classGroup.groupName }}
        </cv-dropdown-item>
      </dropdown-component>

      <div class="student-selection-listing">
        <search-bar
          auto-collapse
          :keyword="studentName"
          active
          filter-type="selection"
          placeholder="Find Students"
          @search="handleSearch"
          @settings-click="toggleSettings"
        >
          <template v-if="params.filterFlags.hideStudentsFromAnySubgroups" #settings>
            <button class="settings">
              <svg-icon name="Settings24" kind="action" />
            </button>
            <content-popup
              :title="'Settings'"
              :visible="showSettingsContentPopup"
              :reference-element="settingsContentPopupRefElement()"
              boundary=".student-selection-listing-from-group-content-subpage"
              :show-close-button="true"
              :show-cancel-button="false"
              :show-save-button="false"
            >
              <template #content>
                <input-checkbox
                  id="hideStudentsFromAllSubgroup"
                  v-model:selected-values="hideStudentsFromAllSubgroupValue"
                  value="hideStudentsFromAllSubgroup"
                  @change="handleHideStudentsCheckboxChange"
                >
                  Hide students from all subgroups
                </input-checkbox>
              </template>
            </content-popup>
          </template>
          <template v-if="!isFromActivityTeams" #filter="{ filterChanged }">
            <filter-selection
              :filter-count="NoOfActiveFilters"
              @click="toggleFilter"
              @remove="removeFilters(filterChanged)"
            />
            <student-selection-listing-from-group-filter
              :visible="showFilterPopup"
              :form-classes="formClasses"
              :level-streams="levelStreams"
              :schools="schools"
              :filter-flags="params.filterFlags"
              :applied-search-filters="appliedSearchFilters"
              @change="filterChanged"
              @close="closeFilterContentPopup"
            />
          </template>
        </search-bar>
        <filter-tag-list v-if="filterTags.length > 0">
          <filter-tag
            v-for="(tag, index) in filterTags"
            :key="index"
            :label="tag.label"
            :count="tag.count"
            @remove="handleRemoveTag(tag)"
          />
        </filter-tag-list>

        <data-table-list in-subpage>
          <template #selection-bar>
            <selection-bar :count="rowsSelected" @clear="handleClearSelection" />
          </template>
          <!--FIXME VUE3: CURRENTLY NOT SUPPORTED
              :showFirstLastPage="footerProps.showFirstLastPage" FIXED
              :mobile-breakpoint="0"
          -->
          <v-data-table
            v-model:page="options.page"
            class="_items-selectable show-first-last"
            v-model:sort-by="options.sortBy"
            :headers="listingHeaders"
            v-model="selectedInner"
            :items-per-page-options="footerProps.itemsPerPageOptions"
            :show-current-page="footerProps.showCurrentPage"
            :items-per-page-text="footerProps.itemsPerPageText"
            :items="filteredStudentList"
            item-value="userId"
            fixed-header
            :loading="isDataTableLoading"
            :mobile-breakpoint="0"
            :items-per-page="options.itemsPerPage"
            show-select
            item-selectable="selectable"
            must-sort
          >
            <template #[`item.formClassCode`]="{ item }">
              <data-table-cell
                class="form-class"
                :class="{ active: sortColumn === 'formClassCode' }"
              >
                <span>{{ item.formClassCode }}</span>
              </data-table-cell>
            </template>
            <template #[`item.classSerialNo`]="{ item }">
              <data-table-cell class="index" :class="{ active: sortColumn === 'classSerialNo' }">
                <span>{{ item.classSerialNo }}</span>
              </data-table-cell>
            </template>
            <template #[`item.name`]="{ item }">
              <data-table-cell class="name" :class="{ active: sortColumn === 'name' }">
                <span>{{ item.name }}</span>
              </data-table-cell>
            </template>
            <template #[`item.levelAndStream`]="{ item }">
              <data-table-cell
                class="level-and-course"
                :class="{ active: sortColumn === 'levelAndStream' }"
              >
                <span>{{ item.levelAndStream }}</span>
              </data-table-cell>
            </template>
            <template #[`item.schoolName`]="{ item }">
              <data-table-cell class="school" :class="{ active: sortColumn === 'schoolName' }">
                <span>{{ item.schoolName }}</span>
              </data-table-cell>
            </template>
          </v-data-table>
        </data-table-list>
      </div>
    </template>
    <template #action-button>
      <cv-button class="add" :disabled="isDisabled" @click="handleAddRecipient">
        <svg-icon name="Plus24" kind="action" />
        <span>{{ t("add_recipient_from_school_button_label") }}</span>
      </cv-button>
    </template>
  </content-subpage>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import {
  ContentSubpageParams,
  useContentSubpage,
} from "@plugins/content-subpage/composables/content-subpage.ts";
import DOMUtils from "@utils/dom.utils";
import { isDefined, isEmpty } from "@utils/var.check";
import StudentGroupApi from "@apis/student.group.api";
import { ADD_STUDENT_GROUP_FILTER_TAG_LABELS } from "@modules/student-group-admin/service/student.group.admin.constant";
import StudentSelectionListingFromGroupFilter from "@modules/student-group/components/StudentSelectionListingFromGroupFilter";
import ObjectUtils from "@utils/object.utils";
import DatatableUtils from "@plugins/vuetify/service/datatable.utils";

// Props
interface AddRecipientsContentSubpageParams extends ContentSubpageParams {
  filterFlags?: {
    group: boolean;
    name: boolean;
    email: boolean;
    school: boolean;
    role: boolean;
  };
  excludedUserIds?: (string | number)[]; // Assuming IDs are string or number; refine if needed
  settings?: Record<string, unknown>;
  selectedStudentGroup?: {
    groupName: string;
    uuid: string;
    studentGroupCollectionId: string | number; // Assuming IDs are string or number; change to string if needed
  }[];
  selectedTeachers: unknown[];
}
interface Props {
  params?: AddRecipientsContentSubpageParams | null;
}

const props = withDefaults(defineProps<Props>(), {
  params: null,
});

// Composables
const { t } = useI18n();
const { passiveApiFailHandler } = useErrorHandler();

const dirtyId = ref(""); // was not overwritten

const {
  titles,
  visible,
  actionHidden,
  actionBack,
  hasParentSubpage,
  isComponentDirty,
  setDirty,
  removeDirty,
  defaultExpose,
} = useContentSubpage(props.params, dirtyId);

// Emits
const emit = defineEmits<(e: "recipientAdded", payload: Record<string, unknown>) => void>();

// Data
const isDataTableLoading = ref(true);
const studentName = ref("");
const listingHeaders = ref<unknown[]>([]);
const footerProps = ref<unknown>({});
const selectedInner = ref<unknown[]>([]);
const selected = ref<unknown[]>([]);
const selectedAndNewlySelected = ref<unknown[]>([]);
const initialSelected = ref<unknown[]>([]);
const options = ref<unknown>({});
const studentList = ref<unknown[]>([]); // Original
const filteredStudentList = ref<unknown[]>([]); // To be displayed
const studentListByGroup = ref<Record<string, unknown>>({});
const parentSubpageName = ref("");
const showFilterPopup = ref(false);
const appliedSearchFilters = ref<Record<string, unknown>>({});
const showSettingsContentPopup = ref(false);
const selectedClassGroup = ref("");
const selectedClassGroupUuid = ref("");
const selectedClassGroupCollectionId = ref("");
const lastSelectedClassGroupUuid = ref("");
const lastSelectedClassGroupCollectionId = ref("");
const selectedRecipients = ref<Record<string, unknown>>({});
const hideStudentsFromAllSubgroupValue = ref<unknown[]>([]);

// Computed
const initialSelectedRecipients = computed(() => props.params?.selectedRecipients ?? {});
const availableClassGroups = computed(() => props.params?.selectedStudentGroup ?? []);

const excludedUserIds = computed(() => props.params?.excludedUserIds ?? []);

//const showDataTable = computed(() => isEmpty(filteredStudentList.value) || isEmpty(studentList.value));

const rowsSelected = computed(() => selectedInner.value.length);

const schools = computed(() => {
  const uniqueSchools = [
    ...new Set(
      studentList.value.flatMap((student) =>
        isEmpty(student.schoolName) ? [] : student.schoolName,
      ),
    ),
  ];
  return uniqueSchools.map((schoolName) => ({
    value: schoolName,
    label: schoolName,
    name: schoolName,
  }));
});

const formClasses = computed(() => {
  const uniqueFormClasses = [
    ...new Set(
      studentList.value.flatMap((student) =>
        isEmpty(student.formClassCode) ? [] : [student.formClassCode],
      ),
    ),
  ];
  return uniqueFormClasses.map((formClass) => ({
    value: formClass,
    label: formClass,
    name: formClass,
  }));
});

const levelStreams = computed(() => {
  const uniqueLevelStreams = [
    ...new Set(
      studentList.value.flatMap((student) =>
        isEmpty(student.levelAndStream) ? [] : [student.levelAndStream],
      ),
    ),
  ];
  return uniqueLevelStreams.map((levelStream) => ({
    value: levelStream,
    label: levelStream,
    name: levelStream,
  }));
});

const NoOfActiveFilters = computed(() => {
  let count = 0;
  if (
    isDefined(appliedSearchFilters.value.levelStreams) &&
    appliedSearchFilters.value.levelStreams.length > 0
  )
    count++;
  if (
    isDefined(appliedSearchFilters.value.formClasses) &&
    appliedSearchFilters.value.formClasses.length > 0
  )
    count++;
  if (isDefined(appliedSearchFilters.value.school) && appliedSearchFilters.value.school !== "")
    count++;
  return count;
});

const isDisabled = computed(() => !isComponentDirty());

const filterTags = computed(() => {
  const filters = Object.keys(appliedSearchFilters.value);
  if (isDefined(filters) && filters.length > 0) {
    return filters
      .map((searchFilter) => {
        let count = 0;
        if (Array.isArray(appliedSearchFilters.value[searchFilter])) {
          count = appliedSearchFilters.value[searchFilter].length;
        } else if (
          isDefined(appliedSearchFilters.value[searchFilter]) &&
          appliedSearchFilters.value[searchFilter] !== ""
        ) {
          count = 1;
        }
        let label = "";
        if (count === 1) {
          label = Array.isArray(appliedSearchFilters.value[searchFilter])
            ? appliedSearchFilters.value[searchFilter].toString()
            : appliedSearchFilters.value[searchFilter];
        } else {
          label = t(ADD_STUDENT_GROUP_FILTER_TAG_LABELS[0][searchFilter]);
        }
        return { type: searchFilter, label: label, count: count };
      })
      .filter((searchFilter) => searchFilter.count !== 0);
  }
  return [];
});

const sortColumn = computed(() =>
  options.value.sortBy.length > 0 ? options.value.sortBy[0].key : "",
);

const backLabel = computed(() => `${t("content_subpage_go_back_link")} ${parentSubpageName.value}`);

const isFromActivityTeams = computed(
  () => isDefined(props.params?.availableStudentsList) && !isEmpty(props.params?.settings),
);

// Methods
const settingsContentPopupRefElement = () => {
  return (
    DOMUtils.elOf(
      document,
      ".student-selection-listing-from-group-content-subpage .search-bar-wrapper",
    )[0] ?? null
  );
};

const fetchStudents = (classGroup) => {
  return new Promise((resolve) => {
    if (classGroup.uuid === "") {
      resolve(null);
      return;
    }
    isDataTableLoading.value = true;

    StudentGroupApi.fetchStudentGroupStudent(classGroup.uuid).then(
      (response) => {
        const students = response.data.filter(
          (student) =>
            student.accountStatus === "ACTIVE" && !excludedUserIds.value.includes(student.userId),
        );
        const massagedStudent = students.map((student) => {
          const classSerialNo = student.classSerialNo > -1 ? student.classSerialNo : "";
          return {
            ...student,
            classSerialNo,
            username: student.name,
            isStudent: true,
          };
        });
        removeFilters();
        studentListByGroup.value[classGroup.studentGroupCollectionId] = massagedStudent;
        studentList.value = studentList.value.concat(massagedStudent);

        isDataTableLoading.value = false;
        resolve();
      },
      () => {
        passiveApiFailHandler();
        resolve();
      },
    );
  });
};

const initDataTableOptions = () => {
  const itemsPerPage = window.matchMedia("(max-width: 671px)").matches ? 10 : 20;
  options.value = {
    page: 1,
    itemsPerPage,
    sortBy: [{ key: "name", order: "asc" }],
  };
  footerProps.value = {
    itemsPerPageOptions: [], // Override to hide
    itemsPerPageText: "", // Override to hide
    itemsPerPageAllText: "", // Override to hide
    showFirstLastPage: true,
    showCurrentPage: true,
  };
  listingHeaders.value = [
    {
      title: t("student_group_detail_header_form_class"),
      class: "form-class",
      key: "formClassCode",
      align: "start",
    },
    {
      title: t("student_group_detail_header_index_no"),
      class: "index",
      key: "classSerialNo",
    },
    { title: t("student_group_detail_header_name"), class: "name", key: "name" },
    {
      title: t("student_group_detail_header_level_course"),
      class: "level",
      key: "levelAndStream",
    },
  ];
};

const handleSearch = (search: { keyword: string; filter: Record<string, unknown> }) => {
  studentName.value = search.keyword;
  appliedSearchFilters.value = search.filter;

  searchStudents();

  closeFilterContentPopup();
};

const searchStudents = () => {
  let filteredStudents = studentListByGroup.value[selectedClassGroupCollectionId.value] ?? [];
  if (isDefined(appliedSearchFilters.value)) {
    const levelStreamFilter = appliedSearchFilters.value.levelStreams;
    if (isDefined(levelStreamFilter) && levelStreamFilter.length !== 0) {
      for (const levelStream of levelStreamFilter) {
        filteredStudents = filteredStudents.filter((student) => {
          if (!isEmpty(student.levelAndStream)) {
            return levelStream.includes(student.levelAndStream);
          }
        });
      }
    }

    const formClassFilter = appliedSearchFilters.value.formClasses;
    if (isDefined(formClassFilter) && formClassFilter.length !== 0) {
      for (const formClass of formClassFilter) {
        filteredStudents = filteredStudents.filter((student) => {
          if (!isEmpty(student.formClassCode)) {
            return formClass.includes(student.formClassCode);
          }
        });
      }
    }

    const schoolFilter = appliedSearchFilters.value.school;
    if (isDefined(schoolFilter) && schoolFilter !== "") {
      filteredStudents = filteredStudents.filter((student) => student.schoolName === schoolFilter);
    }
  }

  const studentNameLocal = studentName.value;
  if (isDefined(studentNameLocal) && studentNameLocal !== "") {
    filteredStudents = filteredStudents.filter(
      (student) => student.name.toLowerCase().indexOf(studentNameLocal.toLowerCase()) !== -1,
    );
  }
  filteredStudentList.value = filteredStudents;
};

const handleClearSelection = () => {
  selectedInner.value = [];
};

const closeFilterContentPopup = () => {
  showFilterPopup.value = false;
};

const toggleFilter = () => {
  showFilterPopup.value = !showFilterPopup.value;
};

const toggleSettings = () => {
  showSettingsContentPopup.value = !showSettingsContentPopup.value;
};

const removeFilters = (filterChanged) => {
  appliedSearchFilters.value = {
    levelStreams: [],
    formClasses: [],
    school: "",
  };
  if (filterChanged) {
    filterChanged({});
  }
  searchStudents();
};

const handleAddRecipient = () => {
  selectedAndNewlySelected.value = [...selected.value];
  handleSelectedStudentsInClassGroups();

  emit("recipientAdded", selectedRecipients.value);

  removeDirty(dirtyId.value);

  actionBack();
};

const handleChangeClassGroupSelected = (value: string) => {
  const [classGroup] = availableClassGroups.value.filter(
    (availableClassGroup) => availableClassGroup.groupName === value,
  );

  lastSelectedClassGroupUuid.value = selectedClassGroupUuid.value;
  lastSelectedClassGroupCollectionId.value = selectedClassGroupCollectionId.value;
  selectedClassGroupUuid.value = classGroup.uuid;
  selectedClassGroupCollectionId.value = classGroup.studentGroupCollectionId;
  selectedClassGroup.value = value;
  selectedAndNewlySelected.value = [...selected.value];
  filteredStudentList.value = studentListByGroup.value[selectedClassGroupCollectionId.value] ?? [];

  handleSelectedStudentsInClassGroups();
};

const initData = () => {
  selectedClassGroupUuid.value = availableClassGroups.value[0]?.uuid ?? "";
  selectedClassGroupCollectionId.value =
    availableClassGroups.value[0]?.studentGroupCollectionId ?? "";
  selectedClassGroup.value = availableClassGroups.value[0]?.groupName ?? "";

  const promiseArray = [];
  availableClassGroups.value.forEach((classGroup) => {
    promiseArray.push(fetchStudents(classGroup));
  });
  Promise.all(promiseArray).then(() => {
    initSelectedStudents();
    filteredStudentList.value =
      studentListByGroup.value[selectedClassGroupCollectionId.value] ?? [];
    handleSelectedTeachersInClassGroups(); // Note: This seems like a typo in original (teachers in student component?); kept as-is
  });
};

const handleRemoveTag = (tag: { type: string }) => {
  if (tag.type === "school") {
    appliedSearchFilters.value = { ...appliedSearchFilters.value, school: "" };
  } else if (tag.type === "levelStreams") {
    appliedSearchFilters.value = { ...appliedSearchFilters.value, levelStreams: [] };
  } else if (tag.type === "formClasses") {
    appliedSearchFilters.value = { ...appliedSearchFilters.value, formClasses: [] };
  }
  searchStudents();
};

const handleHideStudentsCheckboxChange = () => {
  const studentsInAllSubgroup = props.params?.studentsInAllSubgroup ?? [];
  if (hideStudentsFromAllSubgroupValue.value.includes("hideStudentsFromAllSubgroup")) {
    filteredStudentList.value = filteredStudentList.value.filter(
      (student) =>
        !studentsInAllSubgroup.find(
          (studentInSubgroup) => studentInSubgroup.userId === student.userId,
        ),
    );
  } else {
    filteredStudentList.value = studentList.value;
    if (!isEmpty(appliedSearchFilters.value)) {
      handleSearch({ filter: appliedSearchFilters.value });
    }
  }
};

const updateDirtyData = () => {
  if (selectedInner.value.length === 0) {
    removeDirty(dirtyId.value);
  } else {
    setDirty(dirtyId.value);
  }
};

const getStudentData = (student: unknown, studentListIndex: number) => {
  let classGroupUuid;
  let studentGroupCollectionId;
  if (studentListIndex !== -1) {
    classGroupUuid = selectedClassGroupUuid.value;
    studentGroupCollectionId = selectedClassGroupCollectionId.value;
  } else {
    classGroupUuid = isDefined(student.classGroupUuid)
      ? student.classGroupUuid
      : lastSelectedClassGroupUuid.value;
    studentGroupCollectionId = isDefined(student.studentGroupCollectionId)
      ? student.studentGroupCollectionId
      : lastSelectedClassGroupCollectionId.value;
  }

  return {
    ...student,
    classGroupUuid,
    studentGroupCollectionId,
  };
};

// Retain Selected students from other group
const handleSelectedStudentsInClassGroups = () => {
  const selectedStudents = [];
  const selectedStudentsId = [];

  selected.value.forEach((student) => {
    const studentListIndex = studentListByGroup.value[
      selectedClassGroupCollectionId.value
    ].findIndex((recipient) => recipient.userId === student.userId);

    const studentData = getStudentData(student, studentListIndex);

    selectedStudentsId.push(student.userId);
    selectedStudents.push(studentData);
  });

  selectedAndNewlySelected.value.forEach((student) => {
    if (!selectedStudentsId.includes(student.userId)) {
      const studentListIndex = studentListByGroup.value[
        selectedClassGroupCollectionId.value
      ].findIndex((recipient) => recipient.userId === student.userId);

      const studentData = getStudentData(student, studentListIndex);

      if (studentListIndex !== -1) {
        selectedStudents.push(studentData);
      }
      selectedInner.value.push(studentData.userId);
    }
  });
  if (!isEmpty(selectedClassGroupUuid.value)) {
    handleUpdateSelectedRecipients(selectedStudents);
  }
};

const initSelectedStudents = () => {
  selectedInner.value = [];
  if (!isEmpty(initialSelectedRecipients.value)) {
    const recipientInOtherClassGroup = new Set();
    for (const [classGroup, recipients] of Object.entries(initialSelectedRecipients.value)) {
      if (classGroup == selectedClassGroupCollectionId.value) {
        let selectedStudentsInClassGroup = isEmpty(selectedRecipients.value[classGroup])
          ? []
          : selectedRecipients.value[classGroup];
        recipients.students?.forEach((userId: string) => {
          const selectedStudent = studentList.value
            .filter((recipient) => recipient.userId === userId)
            // FIXME: adding new property cause v-model not able to select
            .map((student) => ({
              ...student,
              classGroupUuid: selectedClassGroupUuid.value,
              studentGroupCollectionId: parseInt(classGroup),
            }));
          selectedStudentsInClassGroup = selectedStudentsInClassGroup.concat(selectedStudent);
        });
        selectedRecipients.value[classGroup] = selectedStudentsInClassGroup.filter(
          (recipientA, index: number, self) =>
            index === self.findIndex((recipientB) => recipientB.userId === recipientA.userId),
        );
      } else {
        recipients.students?.forEach((userId: string) => recipientInOtherClassGroup.add(userId));
      }
    }

    // TODO: to fix selection issue
    // check if student is selected in other groups
    recipientInOtherClassGroup.forEach((userId: string) => {
      const student = studentList.value.find((recipient) => recipient.userId === userId);
      if (!isEmpty(student)) {
        const studentData = {
          ...student,
          classGroupUuid: selectedClassGroupUuid.value,
          studentGroupCollectionId: parseInt(selectedClassGroupCollectionId.value),
        };
        if (isDefined(selectedRecipients.value[selectedClassGroupCollectionId.value])) {
          const indexInList = selectedRecipients.value[
            selectedClassGroupCollectionId.value
          ].findIndex((recipient) => recipient.userId === student.userId);
          if (indexInList === -1) {
            selectedRecipients.value[selectedClassGroupCollectionId.value].push(studentData);
          }
        } else {
          selectedRecipients.value[selectedClassGroupCollectionId.value] = [];
          selectedRecipients.value[selectedClassGroupCollectionId.value].push(studentData);
        }
      }
    });
  }

  if (!isEmpty(selectedRecipients.value) && !isEmpty(selectedClassGroupUuid.value)) {
    let recipients = Object.values(selectedRecipients.value).flat(1);
    recipients = recipients.filter(
      (recipientA, index: number, self) =>
        index === self.findIndex((recipientB) => recipientB.userId === recipientA.userId),
    );
    recipients.forEach((recipient) => {
      studentList.value.forEach((student) => {
        if (recipient.userId === student.userId) {
          const selectedIndex = selectedInner.value.findIndex(
            (userId) => userId === recipient.userId,
          );
          if (selectedIndex === -1) selectedInner.value.push(recipient.userId);
        }
      });
    });
    initialSelected.value = selectedInner.value.map((recipient) => recipient);
  }
};

const handleUpdateSelectedRecipients = (newSelectedRecipients) => {
  const recipients = ObjectUtils.deepClone(selectedRecipients.value);
  let existingSelectedClassGroups = [];
  Object.keys(recipients).forEach((classGroupCollectionId) => {
    if (!isEmpty(recipients[parseInt(classGroupCollectionId)])) {
      existingSelectedClassGroups.push(parseInt(classGroupCollectionId));
    }
  });

  existingSelectedClassGroups = Array.from(new Set(existingSelectedClassGroups));
  // Check if Student is removed from selection and if that student is selected in other class groups
  const newSelectedStudents = selected.value.map((student) => student.userId);
  initialSelected.value.forEach((recipient: string) => {
    if (!newSelectedStudents.includes(recipient)) {
      existingSelectedClassGroups.forEach((classGroupCollectionId) => {
        selectedRecipients.value[classGroupCollectionId] = selectedRecipients.value[
          classGroupCollectionId
        ].filter((student) => student.userId !== recipient);
      });
    }
  });
  const existingStudents = Object.values(selectedRecipients.value).flat(1);
  existingStudents.forEach((user) => {
    if (!newSelectedStudents.includes(user.userId)) {
      selectedRecipients.value[selectedClassGroupCollectionId.value] = selectedRecipients.value[
        selectedClassGroupCollectionId.value
      ].filter((student) => student.userId !== user.userId);
    }
  });
  newSelectedRecipients.forEach((student) => {
    if (isEmpty(selectedRecipients.value[selectedClassGroupCollectionId.value])) {
      selectedRecipients.value[selectedClassGroupCollectionId.value] = [];
    } else {
      const studentInSelectedList = selectedRecipients.value[
        selectedClassGroupCollectionId.value
      ].find((obj) => obj.userId === student.userId);
      if (!isEmpty(studentInSelectedList)) return; // student already exist in current list hence no action required
    }

    // selected student in another group
    if (student.studentGroupCollectionId !== selectedClassGroupCollectionId.value) {
      if (isDefined(selectedRecipients.value[student.studentGroupCollectionId])) {
        const indexInList = selectedRecipients.value[student.studentGroupCollectionId].findIndex(
          (recipient) => recipient.userId === student.userId,
        );
        if (indexInList === -1) {
          selectedRecipients.value[student.studentGroupCollectionId].push(student);
        }
      } else {
        selectedRecipients.value[student.studentGroupCollectionId] = [];
        selectedRecipients.value[student.studentGroupCollectionId].push(student);
      }
    } else {
      // find student data
      const selectedStudent = studentListByGroup.value[selectedClassGroupCollectionId.value].filter(
        (recipient) => recipient.userId === student.userId,
      );
      if (!isEmpty(selectedStudent)) {
        selectedRecipients.value[selectedClassGroupCollectionId.value].push(student);
      }
    }
  });
};

// Watchers
watch(
  selectedInner,
  () => {
    updateDirtyData();
    DatatableUtils.filterSelected(
      selectedInner.value,
      filteredStudentList.value,
      "userId",
      selected.value,
    );
  },
  { deep: true },
);

watch(
  schools,
  () => {
    const hasSchool = schools.value.find(
      (school) => school.value === props.params?.preSelectedSchool,
    );
    if (hasSchool) {
      appliedSearchFilters.value = {
        ...appliedSearchFilters.value,
        school: props.params?.preSelectedSchool,
      };
    }
  },
  { deep: true },
);

// Lifecycle
onMounted(() => {
  initDataTableOptions();

  // From created
  if (hasParentSubpage) {
    parentSubpageName.value = titles.value.slice(-1)[0] ?? "";
  }
  titles.value.push(t("announcement_receiver_add_students_title"));
  initData();
});

defineExpose(defaultExpose);
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";

.add-recipients-content-subpage {
  .student-selection-listing {
    margin-top: $spacing-05;

    .search-bar-wrapper {
      padding: $spacing-03;
      background-color: $ui-01;
      border-radius: 10px 10px 0 0;

      .search-bar {
        justify-content: flex-end;

        > div {
          &:not(:first-child) {
            margin-left: $spacing-03;
          }
        }

        .bx--search-input {
          background-color: $ui-background;
          border-radius: 10px;
          &:hover {
            background-color: $hover-ui;
          }
        }

        .button {
          &.close {
            &:hover {
              border-radius: 0 10px 10px 0;
            }
          }
        }

        .filter-selection {
          border-radius: 100%;
          &.has-active-filters {
            border-radius: 10px;
          }
        }
      }
    }

    .filter-tag-list {
      padding: $spacing-03;
    }

    .data-table-list {
      .selection-bar {
        border-radius: 0;
      }
    }
  }
}
</style>
